<template>
  <v-data-table
    :headers="getHeaders"
    :items="
      finalRecipe
        ? items.filter(x => x.est_intermediaire === false)
        : items.filter(x => x.est_intermediaire === true)
    "
    :search="search"
    :loading="loading"
    sort-by="nom"
    item-key="id"
    hide-default-footer
    :page.sync="page"
    :items-per-page="$vuetify.breakpoint.xsOnly ? 6 : 10"
    @page-count="pageCount = $event"
  >
    <template v-slot:item.tags="{ item }" v-if="!$vuetify.breakpoint.xsOnly">
      <v-tooltip bottom v-if="item.data">
        <template v-slot:activator="{ on }">
          <v-btn
            icon
            text
            small
            v-on="on"
            :disabled="item.data.tags.length === 0"
          >
            <v-icon small>fa-hashtag</v-icon>
          </v-btn>
        </template>
        <span>{{ item.data.tags.join(", ") }}</span>
      </v-tooltip>
      <v-icon
        left
        small
        v-if="item.is_deleted === true"
        color="error"
        class="mx-1"
        >fa-trash</v-icon
      >
    </template>
    <template v-slot:item.nom="{ item }">
      <span class="font-capitalize font-weight-bold grey--text text--darken-2">
        {{ item.nom }}</span
      >
    </template>
    <template v-slot:item.actions="{ item }">
      <v-row align="center" justify="end" no-gutters>
        <v-btn
          v-if="getIsAdmin"
          icon
          :small="!$vuetify.breakpoint.xsOnly"
          @click="
            $router.push({
              name: 'RecipeEditor',
              params: { id: item.id }
            })
          "
        >
          <v-icon :small="!$vuetify.breakpoint.xsOnly">far fa-edit</v-icon>
        </v-btn>
        <v-btn
          icon
          :small="!$vuetify.breakpoint.xsOnly"
          @click="
            $router.push({
              name: 'RecipeViewer',
              params: { id: item.id }
            })
          "
        >
          <v-icon :small="!$vuetify.breakpoint.xsOnly"
            >fa-external-link-alt</v-icon
          >
        </v-btn>
      </v-row>
    </template>
    <template v-slot:item.allergens="{ item }">
      <v-chip
        class="block-chip my-1"
        label
        v-for="(ingredient, index) in getIngredientsAllergens(item)
          .filter(x => x !== undefined && x !== null)
          .filter(x =>
            filter.some(a =>
              x.allergens
                .map(g => g.toLowerCase())
                .join(';')
                .includes(a.toLowerCase())
            )
          )"
        :key="index"
      >
        <v-row align="center" justify="space-between" no-gutters>
          <span class="subtitle-2 font-capitalize">{{ ingredient.name }}</span>
          <div v-if="!$vuetify.breakpoint.xsOnly">
            <span
              v-for="(allergen, index) in ingredient.allergens.filter(x =>
                filter.some(a => x.toLowerCase().includes(a.toLowerCase()))
              )"
              :key="index"
              class="mx-1 font-capitalize"
              >{{ allergen }}</span
            >
          </div>
        </v-row>
      </v-chip>
    </template>
    <template v-slot:top>
      <v-row align="center" justify="end">
        <v-col cols="12" sm="6">
          <v-text-field
            dense
            filled
            hide-details
            single-line
            clearable
            prepend-inner-icon="fa-search"
            v-model="search"
            class="no-before"
          ></v-text-field>
        </v-col>
      </v-row>
    </template>
    <template v-slot:footer>
      <v-pagination
        color="accent"
        v-model="page"
        :length="pageCount"
        :total-visible="5"
      ></v-pagination>
    </template>
  </v-data-table>
</template>

<script>
import round10 from "round10";
import { mapGetters } from "vuex";
import { mappers } from "@/mappers";
export default {
  name: "RecipeAllergenGrid",
  props: {
    items: {
      type: Array,
      required: true
    },
    filter: {
      type: Array,
      required: true
    },
    loading: {
      type: Boolean,
      required: false,
      default: false
    },
    finalRecipe: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapGetters(["getProducts", "getOrganisationId", "getIsAdmin"]),
    getHeaders() {
      let headers = [];
      if (this.getIsAdmin) {
        headers = [
          { value: "tags", text: "" },
          { value: "nom", text: "Nom" },
          { value: "allergens", text: "Prov. des allergènes" },
          { value: "actions", text: "" }
        ];
      } else {
        headers = [
          { value: "tags", text: "" },
          { value: "nom", text: "Nom" },
          { value: "allergens", text: "Prov. des allergènes" },
          { value: "actions", text: "" }
        ];
      }
      return headers;
    }
  },
  data() {
    return {
      showDeletedRecipe: false,
      search: "",
      page: 1,
      pageCount: 0
    };
  },
  methods: {
    getRecipeCost(item) {
      const cost = item.ingredients
        .filter(x => x.is_deleted !== true)
        .map(x => this.getIngredientCost(x))
        .reduce((a, b) => a + b, 0);
      return round10.round10(cost, -2);
    },
    getRecipeCostPercent(item) {
      const cost = this.getRecipeCost(item);
      return item.data && item.data.prix_vente > 0
        ? round10.round10((cost / item.data.prix_vente) * 100, -2)
        : 0;
    },
    getIngredientCost(item) {
      const product = this.getProducts.find(
        x => x.id === item.pk_recette_produit
      );
      if (product && product.data) {
        if (
          !mappers.isCustomUnit(item.unite_mesure) &&
          !mappers.isCustomUnit(product.data.unite_mesure)
        ) {
          const itemQtyInProductUnit = this.$units(item.quantite)
            .from(item.unite_mesure)
            .to(product.data.unite_mesure);
          return round10.round10(
            Number(product.data.prix) * itemQtyInProductUnit,
            -2
          );
        } else {
          return round10.round10(Number(product.data.prix) * item.quantite, -2);
        }
      } else {
        return 0;
      }
    },
    getIngredientsAllergens(item) {
      return item.ingredients
        .filter(x => x.is_deleted === false)
        .map(x => {
          if (
            this.getProducts.find(p => p.id === x.pk_recette_produit) &&
            this.getProducts.find(p => p.id === x.pk_recette_produit).data
          ) {
            const product = this.getProducts.find(
              p => p.id === x.pk_recette_produit
            );
            if (product) {
              if (product.data.allergens.length > 0) {
                return {
                  name: product.nom,
                  allergens: product.data.allergens
                };
              } else {
                return {
                  name: product.nom,
                  allergens: []
                };
              }
            } else {
              return;
            }
          }
        });
    }
  }
};
</script>

<style></style>
